import type { Task } from "./types";

export type TaskInsight = {
  stage: string;
  code: string;
  hint: string;
  mailbox: string;
  node: string;
  severity: "info" | "warning" | "error";
};

function compact(value: unknown): string {
  if (!value) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function firstMatch(text: string, patterns: Array<[RegExp, string]>): string {
  for (const [pattern, label] of patterns) {
    if (pattern.test(text)) return label;
  }
  return "";
}

function codeFromText(text: string): string {
  const explicit = text.match(/(?:error[_\s-]?code|code|错误码|err(?:or)?)[\s:：=]+([A-Z0-9_-]{3,})/i);
  if (explicit?.[1]) return explicit[1];
  const http = text.match(/\b(?:HTTP|status)[\s:：=]*(\d{3})\b/i);
  if (http?.[1]) return `HTTP ${http[1]}`;
  const browser = text.match(/\b(ECONNREFUSED|ETIMEDOUT|ECONNRESET|ENOTFOUND|TIMEOUT)\b/i);
  if (browser?.[1]) return browser[1].toUpperCase();
  return "";
}

function mailboxFromText(text: string): string {
  return text.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i)?.[0] || "";
}

function nodeFromText(text: string): string {
  return text.match(/(?:阶段|step)\s*[：:=]\s*([a-z][a-z0-9_-]+)/i)?.[1] || "";
}

export function insightFromMessage(value: unknown): TaskInsight | null {
  const text = compact(value);
  if (!text) return null;
  const stage = firstMatch(text, [
    [/(cloakbrowser|browser|浏览器|指纹)/i, "浏览器启动"],
    [/(proxy|socks|s5|代理|出口 ip|exit_ip)/i, "代理"],
    [/(mail|email|邮箱|验证码|verification)/i, "邮箱验证"],
    [/(2fa|mfa|totp|authenticator|双因子)/i, "2FA"],
    [/(cookie|midjourney|mj)/i, "Midjourney Cookie"],
    [/(discord|login|登录|token)/i, "Discord 登录"]
  ]);
  const code = codeFromText(text);
  const mailbox = mailboxFromText(text);
  const node = nodeFromText(text);
  const hint = firstMatch(text, [
    [/(token).*(invalid|expired|disable|失效|无效|过期|不可用)|(invalid|expired|失效|无效|过期).*(token)/i, "Token 可能已失效，建议先重新执行 Discord 登录或检查导入 token。"],
    [/(cookie).*(invalid|expired|失效|无效|过期)|(invalid|expired|失效|无效|过期).*(cookie)/i, "Cookie 可能已失效，建议重新执行 Midjourney Cookie 任务。"],
    [/(cloakbrowser|浏览器).*(ECONNREFUSED|connection refused|无法连接|连接失败|服务未启动|token)/i, "请确认本地 CloakBrowser-Manager 已启动，并且本地 worker 能访问该服务。"],
    [/(proxy|socks|s5|代理).*(timeout|超时|failed|失败|invalid|不可用|不合格|connection reset|reset by peer|refused|重置)/i, "代理可能不可用或测速失败，可重试代理 profile、地区或强制新代理。"],
    [/(2fa|mfa|totp|双因子).*(invalid|错误|失败|过期|expired)/i, "2FA 密钥或验证码可能不正确，检查账号 2FA Secret 后重试。"],
    [/(mail|email|邮箱).*(timeout|超时|failed|失败|验证码|收不到)/i, "邮箱 API 或邮箱验证码获取异常，检查邮箱 API 配置、public token 和轮询超时。"],
    [/(duplicate|重复|已有排队|已有.*运行)/i, "该账号已有 queued/running 任务，等待完成或在任务队列中做受控维护操作。"]
  ]);
  if (!stage && !code && !hint) return null;
  return {
    stage: stage || "未归类",
    code,
    hint,
    mailbox,
    node,
    severity: hint || code ? "error" : "warning"
  };
}

export function insightFromTask(task: Task): TaskInsight | null {
  const status = String(task.status || "").toLowerCase();
  if (status === "success" && !compact(task.error_message)) return null;
  return insightFromMessage([task.error_message, task.execute_message, task.result].map(compact).filter(Boolean).join("\n"));
}
